import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { message } from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { usersApi } from "../../apis";

const customerSchema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .trim(),
  lastName: yup
    .string()
    .required("Last name is required")
    .trim(),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .trim(),
  phone: yup
    .string()
    .required("Phone number is required")
    .test(
      "phone-format",
      "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
      (value) => {
        // value here is the local number (without +20)
        if (!value) return false;
        // Check if it's exactly 9 digits
        return /^\d{9}$/.test(value);
      },
    ),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Minimum 6 characters"),
});

const AddCustomer = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(customerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      phone: "",
    },
    mode: "onChange",
  });

  const phoneValue = watch("phone");

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    // Remove any non-digit characters
    value = value.replace(/\D/g, "");
    // Limit to 9 digits
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    setValue("phone", value);

    // Clear phone error when user starts typing
    if (errors.phone) {
      clearErrors("phone");
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Validate phone again before submission
      if (!/^\d{9}$/.test(data.phone)) {
        setError("phone", {
          type: "manual",
          message: "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
        });
        setLoading(false);
        return;
      }
      
      // Combine +20 with the phone number for API
      const fullPhoneNumber = `+20${data.phone}`;

      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        password: data.password,
        role: "customer",
        phone: fullPhoneNumber,
      };
      
      await usersApi.create(payload);
      message.success(`${data.firstName} ${data.lastName} created as Customer successfully`);
      history.push("/users/customers");
    } catch (err) {
      const msg = err.response?.data?.message;
      if (Array.isArray(msg)) msg.forEach((m) => message.error(m));
      else message.error(msg || "Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Add New Customer</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel"
                    onClick={() => history.push("/users/customers")}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                {/* Personal Information */}
                <div className="form-group-item">
                  <h5 className="form-title">Personal Information</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.firstName ? "is-invalid" : ""
                          }`}
                          placeholder="Ahmed"
                          {...register("firstName")}
                          disabled={loading}
                        />
                        {errors.firstName && (
                          <small className="text-danger d-block mt-1">
                            {errors.firstName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.lastName ? "is-invalid" : ""
                          }`}
                          placeholder="Hassan"
                          {...register("lastName")}
                          disabled={loading}
                        />
                        {errors.lastName && (
                          <small className="text-danger d-block mt-1">
                            {errors.lastName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Phone <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <span className="me-1">🇪🇬</span> +20
                          </span>
                          <input
                            type="tel"
                            className={`form-control ${
                              errors.phone ? "is-invalid" : ""
                            }`}
                            placeholder="1001234567"
                            value={phoneValue}
                            onChange={handlePhoneChange}
                            disabled={loading}
                          />
                        </div>
                        {errors.phone && (
                          <small className="text-danger d-block mt-1">
                            {errors.phone.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Enter 9 digits after +20 (e.g., 1001234567)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div className="form-group-item mt-2">
                  <h5 className="form-title">Account Details</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          placeholder="ahmed@example.com"
                          {...register("email")}
                          disabled={loading}
                        />
                        {errors.email && (
                          <small className="text-danger d-block mt-1">
                            {errors.email.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Password <span className="text-danger">*</span>
                        </label>
                        <input
                          type="password"
                          className={`form-control ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          placeholder="Min. 6 characters"
                          {...register("password")}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        {errors.password && (
                          <small className="text-danger d-block mt-1">
                            {errors.password.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Minimum 6 characters
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>Role</label>
                        <input
                          type="text"
                          className="form-control"
                          value="Customer"
                          disabled
                          style={{ backgroundColor: "#f5f5f5" }}
                        />
                        <small className="text-muted d-block mt-1">
                          Role is fixed as Customer
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="form-group-item border-0">
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary cancel me-2"
                      onClick={() => history.push("/users/customers")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Creating Customer…
                        </>
                      ) : (
                        "Create Customer"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCustomer;